
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Edit3, CheckCircle, XCircle, AlertTriangle, FileText, Loader2, Eye } from 'lucide-react';
import { format, isPast, parseISO } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface PractitionerDetailsProps {
  practitioner: any;
  getStatusBadge: (status: string) => React.ReactNode;
  getVerificationBadge: (isVerified: boolean) => React.ReactNode;
  formatDate: (date: string) => string;
  onUpdateStatus: (id: string, status: string) => Promise<void>;
  onUpdateVerification: (id: string, isVerified: boolean) => Promise<void>;
}

const PractitionerDetails: React.FC<PractitionerDetailsProps> = ({
  practitioner,
  getStatusBadge,
  getVerificationBadge,
  formatDate,
  onUpdateStatus,
  onUpdateVerification,
}) => {
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [certifications, setCertifications] = useState<any[]>([]);
  const [certsLoading, setCertsLoading] = useState(false);
  const [updatingCertId, setUpdatingCertId] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchCertifications = async () => {
    setCertsLoading(true);
    try {
      const { data, error } = await supabase
        .from('practitioner_certifications')
        .select('*')
        .eq('practitioner_id', practitioner.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Auto-reject expired certifications
      const now = new Date();
      const updates: PromiseLike<any>[] = [];
      const processed = (data || []).map(cert => {
        if (cert.expiry_date && isPast(parseISO(cert.expiry_date)) && cert.status !== 'rejected') {
          updates.push(
            supabase
              .from('practitioner_certifications')
              .update({ status: 'rejected', updated_at: new Date().toISOString() })
              .eq('id', cert.id)
              .then()
          );
          return { ...cert, status: 'rejected' };
        }
        return cert;
      });

      if (updates.length > 0) await Promise.all(updates);
      setCertifications(processed);
    } catch (error) {
      console.error('Error fetching certifications:', error);
    } finally {
      setCertsLoading(false);
    }
  };

  useEffect(() => {
    if (practitioner?.id) {
      fetchCertifications();
    }
  }, [practitioner?.id]);

  const updateCertStatus = async (certId: string, newStatus: 'approved' | 'rejected') => {
    setUpdatingCertId(certId);
    try {
      const { error } = await supabase
        .from('practitioner_certifications')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', certId);

      if (error) throw error;

      // If approving, also update practitioner verification
      if (newStatus === 'approved') {
        const cert = certifications.find(c => c.id === certId);
        const isLicense = /license/i.test(cert?.certification_type || '');

        const practitionerUpdate: Record<string, any> = {
          is_verified: true,
          profile_status: 'approved',
          updated_at: new Date().toISOString(),
        };

        if (isLicense) {
          practitionerUpdate.practice_mode = 'licensed';
          practitionerUpdate.license_status = 'approved';
        }

        await supabase
          .from('practitioners')
          .update(practitionerUpdate)
          .eq('id', practitioner.id);
      }

      toast({ title: `Certification ${newStatus}`, description: `The certification has been ${newStatus}.` });
      fetchCertifications();
    } catch (error) {
      console.error('Error updating certification:', error);
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to update certification status.' });
    } finally {
      setUpdatingCertId(null);
    }
  };

  const getCertStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><AlertTriangle className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  const openCertFile = async (fileUrl: string) => {
    if (fileUrl.startsWith('http')) {
      window.open(fileUrl, '_blank');
      return;
    }
    const { data } = await supabase.storage.from('practitioner_files').createSignedUrl(fileUrl, 300);
    window.open(data?.signedUrl || fileUrl, '_blank');
  };

  const handleStatusUpdate = async (newStatus: string) => {
    setIsLoading(true);
    try {
      await onUpdateStatus(practitioner.id, newStatus);
      setShowStatusDialog(false);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationUpdate = async (isVerified: boolean) => {
    setIsLoading(true);
    try {
      await onUpdateVerification(practitioner.id, isVerified);
      setShowVerificationDialog(false);
    } catch (error) {
      console.error('Error updating verification:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserEmail = async (userId: string, newEmail: string) => {
    if (!newEmail || newEmail === practitioner.email) return;

    setIsUpdatingEmail(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('update-user-email', {
        body: { userId, newEmail }
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Email Updated Successfully',
        description: `Email has been changed from ${data.oldEmail} to ${data.newEmail}`,
      });

      // Update the practitioner email in the parent component
      practitioner.email = newEmail;
      setIsEmailDialogOpen(false);
      setNewEmail('');
      
    } catch (error) {
      console.error('Error updating user email:', error);
      toast({
        variant: 'destructive',
        title: 'Email Update Failed',
        description: error.message || 'Unable to update user email',
      });
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleEmailChange = () => {
    setNewEmail(practitioner.email || '');
    setIsEmailDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Full Name</label>
              <p>{practitioner.first_name} {practitioner.last_name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <div className="flex items-center gap-2">
                <span>{practitioner.email}</span>
                <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleEmailChange}
                      className="h-6 w-6 p-0"
                    >
                      <Edit3 className="h-3 w-3" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Update User Email</DialogTitle>
                      <DialogDescription>
                        Updating email for {practitioner.first_name} {practitioner.last_name}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-4 items-center gap-4">
                        <label htmlFor="current-email" className="text-right text-sm font-medium">
                          Current Email:
                        </label>
                        <div className="col-span-3 text-sm text-gray-600">
                          {practitioner.email}
                        </div>
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <label htmlFor="new-email" className="text-right text-sm font-medium">
                          New Email:
                        </label>
                        <Input
                          id="new-email"
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className="col-span-3"
                          placeholder="Enter new email address"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsEmailDialogOpen(false);
                          setNewEmail('');
                        }}
                        disabled={isUpdatingEmail}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => updateUserEmail(practitioner.id, newEmail)}
                        disabled={isUpdatingEmail || !newEmail || newEmail === practitioner.email}
                      >
                        {isUpdatingEmail ? 'Updating...' : 'Update Email'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Phone</label>
              <p>{practitioner.phone_number || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Registration Date</label>
              <p>{formatDate(practitioner.created_at)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Account Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Profile Status</span>
              <div className="flex items-center gap-2">
                {getStatusBadge(practitioner.profile_status)}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowStatusDialog(true)}
                >
                  Update
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Verification Status</span>
              <div className="flex items-center gap-2">
                {getVerificationBadge(practitioner.is_verified)}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowVerificationDialog(true)}
                >
                  Update
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Professional Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-500">Bio</label>
            <p className="text-sm">{practitioner.bio || 'No bio provided'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Specializations</label>
            <p className="text-sm">{practitioner.specializations || 'Not specified'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Years of Experience</label>
            <p className="text-sm">{practitioner.years_of_experience || 'Not specified'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Education</label>
            <p className="text-sm">{practitioner.education || 'Not provided'}</p>
          </div>
        </CardContent>
      </Card>

      {/* Certifications Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Certifications
          </CardTitle>
          <CardDescription>All certifications submitted by this practitioner</CardDescription>
        </CardHeader>
        <CardContent>
          {certsLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : certifications.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No certifications submitted yet.</p>
          ) : (
            <div className="space-y-4">
              {certifications.map((cert) => {
                const isExpired = cert.expiry_date && isPast(parseISO(cert.expiry_date));
                return (
                  <div key={cert.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="space-y-1 flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-sm">{cert.certificate_name}</h4>
                          {getCertStatusBadge(cert.status)}
                          {isExpired && (
                            <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs">Expired</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{cert.issuing_organization}</p>
                        <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                          <span>Type: {cert.certification_type}</span>
                          <span>Issued: {format(parseISO(cert.issue_date), 'MMM dd, yyyy')}</span>
                          <span>Expiry: {cert.expiry_date ? format(parseISO(cert.expiry_date), 'MMM dd, yyyy') : 'None'}</span>
                        </div>
                        {cert.description && (
                          <p className="text-xs text-muted-foreground mt-1">{cert.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openCertFile(cert.file_url)}
                        >
                          <Eye className="h-3 w-3 mr-1" /> View
                        </Button>
                        {cert.status !== 'approved' && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => updateCertStatus(cert.id, 'approved')}
                            disabled={updatingCertId === cert.id}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" /> Approve
                          </Button>
                        )}
                        {cert.status !== 'rejected' && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => updateCertStatus(cert.id, 'rejected')}
                            disabled={updatingCertId === cert.id}
                          >
                            <XCircle className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Update Dialog */}
      <AlertDialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Practitioner Status</AlertDialogTitle>
            <AlertDialogDescription>
              Choose the new status for this practitioner. This will affect their ability to receive bookings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-3 gap-2 py-4">
            <Button
              variant={practitioner.profile_status === 'pending_review' ? 'default' : 'outline'}
              onClick={() => handleStatusUpdate('pending_review')}
              disabled={isLoading}
            >
              Pending Review
            </Button>
            <Button
              variant={practitioner.profile_status === 'approved' ? 'default' : 'outline'}
              onClick={() => handleStatusUpdate('approved')}
              disabled={isLoading}
            >
              Approved
            </Button>
            <Button
              variant={practitioner.profile_status === 'rejected' ? 'destructive' : 'outline'}
              onClick={() => handleStatusUpdate('rejected')}
              disabled={isLoading}
            >
              Rejected
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Verification Update Dialog */}
      <AlertDialog open={showVerificationDialog} onOpenChange={setShowVerificationDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Verification Status</AlertDialogTitle>
            <AlertDialogDescription>
              Verified practitioners have a verification badge on their profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-2 py-4">
            <Button
              variant={practitioner.is_verified ? 'default' : 'outline'}
              onClick={() => handleVerificationUpdate(true)}
              disabled={isLoading}
            >
              Verified
            </Button>
            <Button
              variant={!practitioner.is_verified ? 'default' : 'outline'}
              onClick={() => handleVerificationUpdate(false)}
              disabled={isLoading}
            >
              Unverified
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PractitionerDetails;
